// Las tablas de presentacion del prototipo del Brain: que icono, que color y
// que etiqueta lleva cada cosa.
//
// Se traen tal cual. Lo que **no** cruza son sus conversaciones de ejemplo
// -AGENTS, BLOCKS_SHOWCASE_CONVERSATION y las otras dos, unas 999 lineas-:
// eran datos inventados para poder dibujar sin backend, y aqui hay backend.

import { Tag as TagIcon, AlertCircle, Calendar, CheckCircle2, Clock, ExternalLink, Facebook, FileText, Globe, Instagram, MessageSquare, Phone, TrendingUp, Users, Webhook, Zap } from 'lucide-react'
import { COLOR_DE_CANAL } from '@/lib/colores-de-canal'

/**
 * El acento de una cifra. Eran cinco colores de paleta elegidos por variedad
 * -azul, violeta, esmeralda, ambar, rosa- y el `grad` era un degradado detras
 * de la tarjeta: una cifra no necesita fondo, ya es lo que manda. Ahora son los
 * tokens, asi que el acento significa algo en vez de solo distinguir.
 */
export const ACCENT_MAP = {
  blue:    { text: 'text-primary',     bg: 'bg-primary/10',     ring: 'ring-primary/20' },
  violet:  { text: 'text-primary',     bg: 'bg-primary/10',     ring: 'ring-primary/20' },
  emerald: { text: 'text-success',     bg: 'bg-success/10',     ring: 'ring-success/20' },
  amber:   { text: 'text-warning',     bg: 'bg-warning/10',     ring: 'ring-warning/20' },
  rose:    { text: 'text-destructive', bg: 'bg-destructive/10', ring: 'ring-destructive/20' },
} as const

export const CHANNEL_ICONS = { whatsapp: MessageSquare, web: Globe, instagram: Instagram, facebook: Facebook, phone: Phone }

export const CHANNEL_LABEL = { whatsapp: 'WhatsApp', web: 'Web', instagram: 'Instagram', facebook: 'Facebook', phone: 'Llamada' }

/**
 * El color de cada canal. Es la excepcion 1 del estandar -marcas de terceros-:
 * el verde de WhatsApp y el rosa de Instagram no son nuestros y no tienen
 * token. «Web» si lo tiene, porque es nuestro.
 */
// El color de marca sale de `lib/colores-de-canal`, que es donde vive una sola
// vez. `web` no es una marca -es «la pagina»-, y por eso es el unico que lleva
// token.
export const CHANNEL_META = {
  whatsapp:  { Icon: MessageSquare, label: 'WhatsApp',  marca: COLOR_DE_CANAL.whatsapp },
  web:       { Icon: Globe,         label: 'Web',       marca: null },
  instagram: { Icon: Instagram,     label: 'Instagram', marca: COLOR_DE_CANAL.instagram },
  facebook:  { Icon: Facebook,      label: 'Facebook',  marca: COLOR_DE_CANAL.facebook },
} as const

export const ENTITY_STATUS = {
  enabled:  { label: 'Habilitado',  dot: 'bg-success', text: 'text-success' },
  disabled: { label: 'Desactivado', dot: 'bg-muted-foreground', text: 'text-muted-foreground' },
  assigned: { label: 'Asignado',    dot: 'bg-success', text: 'text-success' },
} as const

export const ITEM_CONFIGS = {
  doc:  { Icon: FileText,      accent: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/30' },
  url:  { Icon: ExternalLink,  accent: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/30' },
  lead: { Icon: MessageSquare, accent: 'text-success', bg: 'bg-success/10', border: 'border-success/30' },
} as const

export const SOURCE_LABEL = { conversations: 'Conversaciones', mcp: 'MCP', webhook: 'Webhook', kb: 'Knowledge Base' }

export const STATUS_UI = {
  active:       { label: 'Activa',      dot: 'bg-success', text: 'text-success' },
  needs_review: { label: 'Revisar',     dot: 'bg-warning', text: 'text-warning' },
  completed:    { label: 'Completada',  dot: 'bg-primary', text: 'text-primary' },
  archived:     { label: 'Archivada',   dot: 'bg-muted-foreground', text: 'text-muted-foreground' },
} as const

export const STAT_ICONS = { calendar: Calendar, tag: TagIcon, message: MessageSquare, trend: TrendingUp, users: Users, check: CheckCircle2 }

/**
 * Los colores con los que se pinta una etiqueta. Son los que el usuario eligio
 * al crearla, asi que son dato: la misma excepcion que `lib/colores-de-etiqueta`.
 */
/* eslint-disable no-restricted-syntax -- paleta de eleccion del usuario: es dato, no estilo */
export const TAG_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  red:       { bg: 'bg-red-500/15',     text: 'text-red-500',     dot: 'bg-red-500' },
  yellow:    { bg: 'bg-amber-500/15',    text: 'text-amber-500',   dot: 'bg-amber-500' },
  green:     { bg: 'bg-emerald-500/15',  text: 'text-emerald-500', dot: 'bg-emerald-500' },
  blue:      { bg: 'bg-blue-500/15',    text: 'text-blue-500',    dot: 'bg-blue-500' },
  orange:    { bg: 'bg-orange-500/15',   text: 'text-orange-500',  dot: 'bg-orange-500' },
  gray:      { bg: 'bg-zinc-500/15',    text: 'text-zinc-400',    dot: 'bg-zinc-400' },
  purple:    { bg: 'bg-violet-500/15',   text: 'text-violet-500',  dot: 'bg-violet-500' },
  lightblue: { bg: 'bg-sky-500/15',     text: 'text-sky-500',     dot: 'bg-sky-500' },
}
/* eslint-enable no-restricted-syntax */

export const TIMELINE_ICONS = { trigger: Zap, message: MessageSquare, wait: Clock, tag: TagIcon, webhook: Webhook, check: CheckCircle2, alert: AlertCircle }
